import { useMemo } from "react";
import { config } from "./config";
import { devices, type DeviceGroup } from "./devices";
import { useIot } from "./hooks/useIot";
import { useNow } from "./hooks/useNow";
import { DeviceCard } from "./components/DeviceCard";
import { ConnectionChip } from "./components/ConnectionChip";

// Which devices this build shows, set via VITE_DEVICE_GROUP at build time:
//   "jetson" (gpu.variphi.com)  → Jetson only
//   "others" (jetson.variphi.com) or unset → all devices (includes Jetson)
const DEVICE_GROUP = import.meta.env.VITE_DEVICE_GROUP as DeviceGroup | undefined;
const shownDevices =
  DEVICE_GROUP === "jetson"
    ? devices.filter((d) => d.group === "jetson")
    : devices;

export default function App() {
  const { state } = useIot();
  const now = useNow();

  const summary = useMemo(() => {
    let online = 0;
    let offline = 0;
    for (const meta of shownDevices) {
      const d = state.devices[meta.id];
      if (!d) continue;
      const stale =
        d.lastTelemTs != null &&
        now - d.lastTelemTs > config.offlineThresholdMs;
      if (d.status === "offline" || stale) offline++;
      else if (d.lastTelemTs != null) online++;
    }
    return { online, offline, total: shownDevices.length };
  }, [state.devices, now]);

  return (
    <div className="app">
      <header className="topbar">
        <div className="topbar-inner">
          <div className="brand">
            <h1>Device Status</h1>
            <div className="brand-sub">
              <span>
                <strong>{summary.online}</strong> up
              </span>
              <span className="dot-sep" aria-hidden="true">
                •
              </span>
              <span
                className={summary.offline > 0 ? "summary-bad" : undefined}
              >
                <strong>{summary.offline}</strong> down
              </span>
              <span className="dot-sep" aria-hidden="true">
                •
              </span>
              <span>{summary.total} total</span>
            </div>
          </div>
          <ConnectionChip
            connected={state.connected}
            lastError={state.lastError}
          />
        </div>
      </header>

      <main className="wrap">
        <div className="devices">
          {shownDevices.map((meta) => {
            const device = state.devices[meta.id];
            if (!device) {
              return (
                <section className="device device-skeleton" key={meta.id}>
                  <header className="device-header">
                    <h2 className="device-title">{meta.name}</h2>
                    <span className="device-id">
                      <code>{meta.id}</code>
                    </span>
                  </header>
                  <div className="skeleton-line skeleton-line-lg" />
                  <div className="skeleton-tiles">
                    <div className="skeleton-tile" />
                    <div className="skeleton-tile" />
                    <div className="skeleton-tile" />
                  </div>
                </section>
              );
            }
            return (
              <DeviceCard
                key={meta.id}
                connection={{
                  connected: state.connected,
                  lastError: state.lastError,
                }}
                device={device}
              />
            );
          })}
        </div>
      </main>
    </div>
  );
}
